// Carry the landing-page demo dish (custom name + photo + ingredients) into
// the user's account as their first real recipe, right after successful sign-up.
// This makes the "Save This Recipe" landing CTA actually pay off.
//
// Called from Register.jsx (password sign-up) and AuthCallback.jsx (Google).
// No-op when the visitor hasn't customised anything.

import api from "@/lib/api";

const NAME_KEY = "cs_demo_dish_name";
const PHOTO_KEY = "cs_demo_dish_photo";
const INGREDIENTS_KEY = "cs_demo_dish_ingredients";
const DEFAULT_NAME = "Signature Atlantic Salmon";

function dataUrlToFile(dataUrl, filename) {
  const [meta, base64] = dataUrl.split(",");
  const mime = /:(.*?);/.exec(meta)?.[1] || "image/jpeg";
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new File([bytes], filename, { type: mime });
}

// Get-or-create an ingredient by name. The backend now rejects duplicate names
// (case-insensitive), so on a 400 clash we fall back to the existing row.
const UNIT_KIND_BY_UNIT = {
  g: "weight",
  kg: "weight",
  ml: "volume",
  L: "volume",
  each: "count",
};

async function getOrCreateIngredient(row, pantryCache) {
  const name = (row.name || "").trim();
  if (!name) return null;

  // Fast path: check the cached list we fetched once.
  const hit = pantryCache.find((p) => p.name.toLowerCase() === name.toLowerCase());
  if (hit) return hit.id;

  const unit = row.unit || "g";
  const payload = {
    name,
    category: "food",
    purchase_qty: Number(row.per_unit) || 1000,
    purchase_unit: unit,
    purchase_unit_kind: UNIT_KIND_BY_UNIT[unit] || "weight",
    purchase_price: Number(row.price) || 0,
    yield_percent: Math.min(100, Math.max(1, Number(row.yield_pct) || 100)),
  };
  try {
    const { data } = await api.post("/ingredients", payload);
    if (data?.id) {
      pantryCache.push({ id: data.id, name });
      return data.id;
    }
  } catch (err) {
    // 400 = duplicate. Reload the pantry once and try to find it.
    if (err?.response?.status === 400) {
      try {
        const { data } = await api.get("/ingredients");
        if (Array.isArray(data)) {
          pantryCache.splice(0, pantryCache.length, ...data.map((d) => ({ id: d.id, name: d.name })));
          const found = pantryCache.find((p) => p.name.toLowerCase() === name.toLowerCase());
          if (found) return found.id;
        }
      } catch (_) {}
    }
  }
  return null;
}

export async function carryDemoDishIntoAccount() {
  let dishName = "";
  let photoDataUrl = "";
  let demoIngredients = [];
  try {
    dishName = (localStorage.getItem(NAME_KEY) || "").trim();
    photoDataUrl = localStorage.getItem(PHOTO_KEY) || "";
    const raw = localStorage.getItem(INGREDIENTS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) demoIngredients = parsed;
    }
  } catch (_) {
    return null;
  }

  const isCustomised =
    (dishName && dishName !== DEFAULT_NAME) ||
    photoDataUrl ||
    demoIngredients.length > 0;
  if (!isCustomised) return null;

  const name = dishName || DEFAULT_NAME;
  let imagePath = null;

  // Upload the photo (if any) to Emergent object storage.
  if (photoDataUrl) {
    try {
      const file = dataUrlToFile(photoDataUrl, "landing-dish.jpg");
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      imagePath = data?.path || data?.url || null;
    } catch (_) {
      // Silent: still create the recipe without a photo.
    }
  }

  // Materialise the demo ingredients as real pantry rows first, then link them.
  const pantryCache = [];
  const recipeLines = [];
  for (const row of demoIngredients) {
    if (!row?.name || !Number(row?.qty)) continue;
    const ingredientId = await getOrCreateIngredient(row, pantryCache);
    if (ingredientId) {
      recipeLines.push({
        ingredient_id: ingredientId,
        quantity: Number(row.qty),
        unit: row.unit || "g",
      });
    }
  }

  // Create the recipe with the resolved lines. If nothing resolved (all rows
  // missing names), still create the recipe so the chef sees their dish name.
  let createdId = null;
  try {
    const { data } = await api.post("/recipes", {
      name,
      bracket: "food",
      servings: 1,
      image_url: imagePath || undefined,
      ingredients: recipeLines,
      description:
        recipeLines.length > 0
          ? "Carried over from your landing-page recipe. Edit any ingredient in the pantry to refine your plate cost."
          : "Carried over from your landing-page demo. Add your ingredients to compute a real plate cost.",
    });
    createdId = data?.id || null;
  } catch (_) {
    return null;
  }

  // Only clear the localStorage after a successful recipe insert.
  try {
    localStorage.removeItem(NAME_KEY);
    localStorage.removeItem(PHOTO_KEY);
    localStorage.removeItem(INGREDIENTS_KEY);
    if (createdId) localStorage.setItem("cs_just_carried_dish_id", createdId);
  } catch (_) {}
  return { id: createdId, name, hasPhoto: !!imagePath, lines: recipeLines.length };
}
